import React, { useEffect, useMemo, useRef, useState } from "react";
import "swiper/css/bundle";
import "./styles.css";
import { connect } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { RootState } from "../../../../store/index.ts";
import { HousingsActionCreator } from "../../../../store/housingsActions.ts";
import {
  selectSharedData,
  selectSharedError,
  selectSharedHousingData,
  selectSharedPending,
} from "../../../../store/selectors.ts";
import { HousingInfo } from "../../../HousingComplex/components/HousingInfo";
import { Error } from "../../../../components/common/Error";
import { BrandProvider, useBranding } from "../../../../integrations/branding";
import { getInstallmentOptionName } from "./components/FullInfo/utils";
import { API_ENDPOINTS } from "../../../../constants/common";
import { apiClient, formatInternationalPhone, getPhoneHref } from "../../../../utils";
import {
  ANALYTICS_EVENTS,
  getAnalyticsContextSnapshot,
  getCalculationAnalyticsProperties,
  trackAnalyticsEvent,
} from "../../../../analytics";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value).replace(/\s/g, "").replace(",", ".");
  const numeric = Number(normalized);

  return Number.isFinite(numeric) ? numeric : null;
};

const formatMetricValue = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = toNumber(value);

  if (numeric !== null) {
    const formatted = new Intl.NumberFormat("ru-RU", {
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);

    return suffix ? `${formatted} ${suffix}` : formatted;
  }

  return suffix ? `${value} ${suffix}` : String(value);
};

const getAgentIdFromShared = (data) =>
  data?.user_id || data?.userId || data?.agent_id || data?.agentId || null;

const normalizeSocialValue = (value) => String(value || "").replace(/\D/g, "");

const normalizeAgentResponse = (payload) => {
  const user = payload?.data || payload?.user || payload || null;
  const socials = Array.isArray(user?.socials) ? user.socials : [];
  const getSocial = (type) =>
    socials.find((item) => item?.type === type)?.value || user?.[type] || null;

  return {
    id: user?.id ?? null,
    name: user?.name || "Агент",
    phone: user?.phone || null,
    whatsapp: getSocial("whatsapp"),
    telegram: getSocial("telegram"),
    instagram: getSocial("instagram"),
    socials,
  };
};

const normalizeBrandContact = (config) => {
  const showcase = config?.showcase;
  const contact = {
    id: config?.id ?? null,
    name: showcase?.contactName || config?.displayName || "Бренд",
    phone: showcase?.phone || null,
    whatsapp: showcase?.whatsapp || showcase?.whatsappNumber || null,
    telegram: showcase?.telegram || null,
    instagram: showcase?.instagram || null,
  };

  return contact.phone || contact.whatsapp || contact.telegram || contact.instagram
    ? contact
    : null;
};

const AgentContactCard = ({ agent, branded = false, onContactClick }) => {
  const phone = formatInternationalPhone(agent?.phone);
  const whatsappSource = agent?.whatsapp || agent?.phone;
  const whatsappDigits =
    formatInternationalPhone(whatsappSource)?.replace(/\D/g, "") ||
    normalizeSocialValue(whatsappSource);
  const whatsappLink = whatsappDigits ? `https://wa.me/${whatsappDigits}` : null;
  const telLink = getPhoneHref(phone);
  const telegramValue = String(agent?.telegram || "").replace(/^@/, "").trim();
  const telegramLink = telegramValue ? `https://t.me/${telegramValue}` : null;
  const instagramValue = String(agent?.instagram || "").replace(/^@/, "").trim();
  const instagramLink = instagramValue
    ? `https://instagram.com/${instagramValue}`
    : null;
  const socialItems = [
    telLink
      ? {
          href: telLink,
          label: "Позвонить",
          tone: "secondary",
          channel: "phone",
        }
      : null,
    whatsappLink
      ? {
          href: whatsappLink,
          label: "WhatsApp",
          tone: "primary",
          external: true,
          channel: "whatsapp",
        }
      : null,
    telegramLink
      ? {
          href: telegramLink,
          label: "Telegram",
          tone: "secondary",
          external: true,
          channel: "telegram",
        }
      : null,
    instagramLink
      ? {
          href: instagramLink,
          label: "Instagram",
          tone: "secondary",
          external: true,
          channel: "instagram",
        }
      : null,
  ].filter(Boolean);

  if (socialItems.length === 0) {
    return null;
  }

  return (
    <section
      className={`agent-contact ${branded ? "agent-contact-branded" : ""}`.trim()}
    >
      <div className="agent-contact_header">
        <div className="agent-contact_eyebrow">Связь с агентом</div>
        <h2 className="agent-contact_title">{agent?.name || "Агент"}</h2>
      </div>

      <div className="agent-contact_actions">
        {socialItems.map((item) => (
          <a
            key={`${item.label}-${item.href}`}
            href={item.href}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noreferrer" : undefined}
            onClick={() => onContactClick?.(item.channel)}
            className={
              item.tone === "primary"
                ? "agent-contact_primary"
                : "agent-contact_secondary"
            }
          >
            {item.label}
          </a>
        ))}
      </div>
    </section>
  );
};

const SharedCalcPriorityCards = ({ items }) => (
  <div className="shared-calc_priority-mobile" aria-label="Детали предложения">
    <div className="shared-calc_priority-header">Детали предложения</div>
    <div className="shared-calc_priority-grid">
      {items.map((item) => (
        <div
          key={item.label}
          className={`shared-calc_priority-card ${item.wide ? "is-wide" : ""}`.trim()}
        >
          <div className="shared-calc_priority-value">{item.value}</div>
          <div className="shared-calc_priority-label">{item.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const getPriorityItems = (data) =>
  [
    { label: "Квадратура", value: formatMetricValue(data?.area, "м²"), wide: false },
    { label: "Этаж", value: data?.floor ? String(data.floor) : null, wide: false },
    { label: "Полная стоимость", value: formatMetricValue(data?.fullPrice, "₽"), wide: false },
    {
      label: "Взнос",
      value: formatMetricValue(data?.firstPayment, "₽"),
      wide: true,
    },
    {
      label: "Ежемесячный платеж",
      value: formatMetricValue(data?.monthlyPayment, "₽"),
      wide: true,
    },
  ].filter((item) => item.value);

const SharedCalcSections = ({ data, branded = false }) => {
  const rest = useMemo(() => toNumber(data?.rest), [data?.rest]);
  const restAfterMatCap = useMemo(
    () => toNumber(data?.restAfterMatCap),
    [data?.restAfterMatCap],
  );
  const matCapAmount = useMemo(() => {
    if (rest == null || restAfterMatCap == null) {
      return null;
    }

    const diff = rest - restAfterMatCap;
    return diff > 0 ? diff : null;
  }, [rest, restAfterMatCap]);

  const summaryItems = [
    { label: "Полная стоимость", value: formatMetricValue(data?.fullPrice, "₽") },
    { label: "Ежемесячный платеж", value: formatMetricValue(data?.monthlyPayment, "₽") },
    { label: "Первоначальный взнос", value: formatMetricValue(data?.firstPayment, "₽") },
    { label: "Остаток", value: formatMetricValue(data?.rest, "₽") },
  ].filter((item) => item.value);

  const detailItems = [
    { label: "Тип квартиры", value: data?.flatType },
    { label: "Этаж", value: data?.floor },
    { label: "Квадратура", value: formatMetricValue(data?.area, "м²") },
    { label: "Стоимость квадрата", value: formatMetricValue(data?.areaWorth, "₽") },
    { label: "Срок рассрочки", value: formatMetricValue(data?.installmentPeriod, "мес.") },
    {
      label: "Процент первого взноса",
      value: getInstallmentOptionName(data?.installmentOption),
    },
    { label: "Процент наценки", value: data?.installmentPercent },
    { label: "Маткап", value: formatMetricValue(matCapAmount, "₽") },
    {
      label: "Остаток",
      value: formatMetricValue(restAfterMatCap, "₽"),
    },
  ].filter((item) => item.value);

  return (
    <div className={`shared-calc_cards ${branded ? "is-branded" : ""}`.trim()}>
      <section className={`shared-calc_section shared-calc_section-summary ${branded ? "is-branded" : ""}`.trim()}>
        <div className="shared-calc_section-header">
          <h2 className="shared-calc_title">Детали предложения</h2>
        </div>

        <div className="shared-calc_summary-grid">
          {summaryItems.map((item) => (
            <div key={item.label} className="shared-calc_metric-card">
              <div className="shared-calc_metric-label">{item.label}</div>
              <div className="shared-calc_metric-value">{item.value}</div>
            </div>
          ))}
        </div>
      </section>

      <section className={`shared-calc_section ${branded ? "is-branded" : ""}`.trim()}>
        <div className="shared-calc_section-header">
          <h2 className="shared-calc_title">Условия расчета</h2>
        </div>

        <div className="shared-calc_detail-list">
          {detailItems.map((item) => (
            <div key={item.label} className="shared-calc_detail-row">
              <span className="shared-calc_detail-label">{item.label}</span>
              <span className="shared-calc_detail-value">{item.value}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const SharedCalcContent = ({
  data,
  housing,
  agent,
  branded = false,
  housingContent,
  onContactClick,
}) => (
  <div className={`shared-calc_page ${branded ? "is-branded" : ""}`.trim()}>
    <div className="shared-calc_showcase">
      <div className="shared-calc_container">
        <HousingInfo
          housing={housing}
          isAuth={branded}
          content={housingContent}
          priorityContent={<SharedCalcPriorityCards items={getPriorityItems(data)} />}
        />

        <div className="shared-calc_grid">
          <div className="shared-calc_main">
            <SharedCalcSections data={data} branded={branded} />
          </div>

          <aside className="shared-calc_aside">
            <AgentContactCard
              agent={agent}
              branded={branded}
              onContactClick={onContactClick}
            />
          </aside>
        </div>
      </div>
    </div>
  </div>
);

const BrandSharedShell = ({ children }) => {
  const { config } = useBranding();
  const palette = config.showcase.palette;

  return (
    <div
      className="brand-showcase"
      style={
        {
          "--brand-page-background": palette.pageBackground,
          "--brand-page-background-start": palette.pageBackgroundStart,
          "--brand-page-background-end": palette.pageBackgroundEnd,
          "--brand-surface": palette.surface,
          "--brand-surface-alt": palette.surfaceAlt,
          "--brand-surface-elevated": palette.surfaceElevated,
          "--brand-border": palette.border,
          "--brand-accent": palette.accent,
          "--brand-accent-strong": palette.accentStrong,
          "--brand-accent-contrast": palette.accentContrast,
          "--brand-text-primary": palette.textPrimary,
          "--brand-text-secondary": palette.textSecondary,
          "--brand-grid-line": palette.gridLine,
          "--brand-glow-primary": palette.glowPrimary,
          "--brand-glow-secondary": palette.glowSecondary,
          "--brand-badge-background": palette.badgeBackground,
          "--brand-badge-text": palette.badgeText,
          "--brand-tab-active-background": palette.tabActiveBackground,
          "--brand-tab-active-text": palette.tabActiveText,
          "--brand-input-background": palette.inputBackground,
          "--brand-input-highlight": palette.inputHighlight,
          "--brand-secondary-button-background": palette.secondaryButtonBackground,
          "--brand-secondary-button-text": palette.secondaryButtonText,
          "--brand-secondary-button-border": palette.secondaryButtonBorder,
          "--brand-skeleton-base": palette.skeletonBase,
          "--brand-skeleton-highlight": palette.skeletonHighlight,
          "--brand-logo-width": `${config.showcase.logoWidth}px`,
          "--brand-logo-image-height": `${config.showcase.logoImageHeight}px`,
          "--brand-logo-text-size": `${config.showcase.logoTextSize}px`,
          "--brand-logo-text-color": config.showcase.logoTextColor,
        } as React.CSSProperties
      }
    >
      <div className="brand-showcase_shell">
        <header className="brand-showcase_header">
          <div className="brand-showcase_topbar">
            <div className="brand-showcase_logo-wrap">
              <div
                className={`brand-showcase_logo ${
                  config.showcase.logoLayout === "text-bottom"
                    ? "is-text-bottom"
                    : "is-text-right"
                } ${
                  config.showcase.logoMode === "image"
                    ? "is-image-only"
                    : config.showcase.logoMode === "image-text"
                      ? "is-image-text"
                      : "is-text-only"
                }`}
              >
                {(config.showcase.logoMode === "image" ||
                  config.showcase.logoMode === "image-text") &&
                config.showcase.logoSrc ? (
                  <img
                    src={config.showcase.logoSrc}
                    alt={config.showcase.logoAlt || config.displayName}
                  />
                ) : null}

                {(config.showcase.logoMode === "text" ||
                  config.showcase.logoMode === "image-text") &&
                config.showcase.logoText ? (
                  <span>{config.showcase.logoText}</span>
                ) : null}
              </div>
            </div>

            <div className="brand-showcase_section-label">
              {config.showcase.heroLabel ? (
                <>
                  <span className="brand-showcase_section-line" />
                  <span>{config.showcase.heroLabel}</span>
                </>
              ) : null}
            </div>
          </div>

          {config.showcase.heroTitle || config.showcase.heroDescription ? (
            <div className="brand-showcase_intro">
              {config.showcase.heroTitle ? (
                <h1 className="brand-showcase_title">{config.showcase.heroTitle}</h1>
              ) : null}
              {config.showcase.heroDescription ? (
                <p className="brand-showcase_description">
                  {config.showcase.heroDescription}
                </p>
              ) : null}
            </div>
          ) : null}
        </header>

        <main className="brand-showcase_content">{children}</main>
      </div>
    </div>
  );
};

const BrandedSharedCalc = ({ data, housing, onContactClick }) => {
  const { config } = useBranding();
  const brandContact = normalizeBrandContact(config);
  return (
    <BrandSharedShell>
      <SharedCalcContent
        data={data}
        housing={housing}
        agent={brandContact || data?.agent}
        branded
        housingContent={config.housingDetails}
        onContactClick={onContactClick}
      />
    </BrandSharedShell>
  );
};

export const _HousingComplexShared = ({
  error,
  pending,
  data,
  housing,
  fetchSharedData,
}) => {
  const sharedDataId = useParams()?.id;
  const [searchParams] = useSearchParams();
  const brandSlug = searchParams.get("brand") || undefined;
  const [agent, setAgent] = useState(null);
  const trackedOpenRef = useRef<string | null>(null);

  const sharedAnalyticsProperties = useMemo(() => {
    const creatorContext =
      data?.analyticsContext && typeof data.analyticsContext === "object"
        ? data.analyticsContext
        : {};
    const creatorProperties = Object.entries(creatorContext).reduce(
      (result, [key, value]) => {
        if (
          value === null ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          result[`creator_${key}`] = value;
        }
        return result;
      },
      {},
    );
    const attributedAgentId =
      creatorContext.actor_user_id || getAgentIdFromShared(data) || undefined;
    const viewer = getAnalyticsContextSnapshot();

    return {
      ...getCalculationAnalyticsProperties(data, housing),
      ...creatorProperties,
      share_id: data?.id,
      share_code: sharedDataId,
      attributed_agent_user_id: attributedAgentId,
      viewer_is_creator: Boolean(
        viewer.actor_user_id &&
          attributedAgentId &&
          String(viewer.actor_user_id) === String(attributedAgentId),
      ),
      branded_showcase: Boolean(brandSlug),
      brand_id: brandSlug,
    };
  }, [brandSlug, data, housing, sharedDataId]);

  useEffect(() => {
    fetchSharedData(sharedDataId);
  }, [fetchSharedData, sharedDataId]);

  useEffect(() => {
    if (
      pending ||
      error ||
      !data ||
      !sharedDataId ||
      trackedOpenRef.current === sharedDataId
    ) {
      return;
    }

    trackedOpenRef.current = sharedDataId;
    trackAnalyticsEvent(
      ANALYTICS_EVENTS.SHARED_CALCULATION_OPENED,
      sharedAnalyticsProperties,
    );
  }, [
    data,
    error,
    pending,
    sharedAnalyticsProperties,
    sharedDataId,
  ]);

  const handleContactClick = (channel) => {
    trackAnalyticsEvent(
      ANALYTICS_EVENTS.SHARED_CALCULATION_CONTACT_CLICKED,
      {
        ...sharedAnalyticsProperties,
        contact_channel: channel,
      },
    );
  };

  useEffect(() => {
    const agentId = getAgentIdFromShared(data);

    if (!agentId) {
      setAgent(null);
      return;
    }

    let cancelled = false;

    apiClient
      .get(`${API_ENDPOINTS.getUserInfoById}${agentId}`)
      .then((response) => {
        if (cancelled) {
          return;
        }

        setAgent(normalizeAgentResponse(response?.data));
      })
      .catch(() => {
        if (!cancelled) {
          setAgent(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data]);

  if (pending) {
    return null;
  }

  if (error) {
    return <Error retryCallback={() => fetchSharedData(sharedDataId)} />;
  }

  if (brandSlug) {
    return (
      <BrandProvider brandSlug={brandSlug} isShowcase>
        <BrandedSharedCalc
          data={{ ...data, agent }}
          housing={housing}
          onContactClick={handleContactClick}
        />
      </BrandProvider>
    );
  }

  return (
    <SharedCalcContent
      data={data}
      housing={housing}
      agent={agent}
      onContactClick={handleContactClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  pending: selectSharedPending(state),
  error: selectSharedError(state),
  data: selectSharedData(state),
  housing: selectSharedHousingData(state),
});

const mapDispatchToProps = {
  fetchSharedData: HousingsActionCreator.fetchSharedData,
};

export const HousingComplexShared = connect(
  mapStateToProps,
  mapDispatchToProps,
)(_HousingComplexShared);
